/* eslint-disable dot-notation */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  buyItems,
  fetchAvailableItems,
  sellItems,
} from 'actions/exchange';

import getQuantities from 'selectors/exchange/getQuantities';

import { playerCanAffordTransaction } from 'components/Exchange/utils';
import { MAX_SELL_AMOUNT } from 'components/Exchange/constants';

import ExchangeUI from './ExchangeUIComponent';

class ExchangeUIContainer extends Component {
  state = {
    sellAmount: 1,
  }

  componentDidMount = () => {
    // Immediately set disabled if the player can't even afford to buy/sell 1 of this item
    this.updateDisabledState();
  }

  handleIncrement = (amount) => {
    const { sellAmount: oldSellAmount } = this.state;
    const sellAmount = (+oldSellAmount || 0) + Number(amount);
    // Update sell amount (clamping it to possible values) then update disabled state
    this.setState({ sellAmount: this.clampAmount(sellAmount) }, this.updateDisabledState);
  }

  handleChange = (e) => {
    const sellAmount = e.target.value;

    // Update sell amount then update disabled state
    this.setState({ sellAmount }, this.updateDisabledState);
  }

  handleSubmit = (e) => {
    e.preventDefault();

    const {
      activeItem,
      buying,
      dispatch,
      onTransactionComplete,
      shops,
    } = this.props;
    const { sellAmount } = this.state;
    const { quality, purchaseQuality } = activeItem;

    const transactionData = {
      quality,
      purchaseQuality,
      availabilityId: activeItem.availability.id,
      amount: sellAmount,
    };

    // The action data are the same whether we're buying or selling;
    // it's just the API connection that's different
    const action = buying ? buyItems(transactionData) : sellItems(transactionData);
    return dispatch(action)
      .then((data) => {
        const { possessionsChanged: changes, message: successMessage } = data;
        // We should update the UI to show the success message
        onTransactionComplete({ successMessage });

        // Check whether, by buying or selling stuff, we have acquired something new
        const myItems = shops[null].items;
        if (changes && changes.some(q => isNewQuality(q, myItems))) {
          // Dispatch a full on re-fetch of sellable items
          dispatch(fetchAvailableItems(null, { background: true }));
        }

        function isNewQuality(possession, items) {
          // If we've received some Echoes, then definitely return false; Echoes don't
          // appear in any shop
          if (possession.category === 'Currency') {
            return false;
          }
          // If we don't already have an Availability with this Quality, return true
          return items.map(_ => _.availability.quality.id).indexOf(possession.id) < 0;
        }
      });
  }

  clampAmount = (newSellAmount) => {
    // We're clamping the sell amount to [0, max], where max is determined differently
    // depending on whether we're buying or selling
    const maxAmount = this.getMaxAmount();
    const clampedAmount = Math.max(0, Math.min(newSellAmount, maxAmount));
    return clampedAmount;
  }

  getMaxAmount = () => {
    const { activeItem, buying, quantities } = this.props;
    const { cost, purchaseQuality, quality } = activeItem.availability;
    // If we are buying an item, then the maximum amount is the largest number
    // that we can afford, given the purchase quality and how much of _that_ we have
    if (buying) {
      const playerCurrencyLevel = quantities[purchaseQuality.id] || 0;
      return Math.floor(playerCurrencyLevel / cost);
    }
    // Otherwise, we can sell up to as many of the item as we have in our inventory
    const quantityPlayerHas = quantities[quality.id];
    return quantityPlayerHas;
  }

  updateDisabledState = () => {
    const { sellAmount } = this.state;

    if (sellAmount === 0) {
      return this.setState({ disabled: true });
    }

    // ... but if we *can* parse it as a value, then set an error message if
    // the user is trying to sell too many at once

    if (parseInt(sellAmount, 10) > MAX_SELL_AMOUNT) {
      return this.setState({
        disabled: true,
      });
    }

    // If the player can't afford this (buying or selling), then disable
    if (!playerCanAffordTransaction({ ...this.props, ...this.state })) {
      return this.setState({
        disabled: true,
        errorMessage: null,
      });
    }

    return this.setState({
      errorMessage: null,
      disabled: Number.isNaN(+sellAmount),
    });
  }

  render() {
    const {
      activeItem,
      buying,
      onRequestClose,
      quantities,
    } = this.props;
    const { disabled, errorMessage, sellAmount } = this.state;

    return (
      <ExchangeUI
        activeItem={activeItem}
        buying={buying}
        countCharacterAlreadyHas={quantities[activeItem.availability.quality.id]}
        disabled={disabled}
        errorMessage={errorMessage}
        maxAmount={this.getMaxAmount()}
        onChange={this.handleChange}
        onIncrement={this.handleIncrement}
        onRequestClose={onRequestClose}
        onSubmit={this.handleSubmit}
        sellAmount={sellAmount}
      />
    );
  }
}

ExchangeUIContainer.displayName = 'ExchangeUIContainer';

ExchangeUIContainer.propTypes = {
  activeItem: PropTypes.shape({

  }).isRequired,
  buying: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onTransactionComplete: PropTypes.func.isRequired,
  quantities: PropTypes.shape({}).isRequired,
  shops: PropTypes.shape({}).isRequired,
};

const mapStateToProps = (state) => {
  const {
    exchange: {
      activeItem,
      isFetchingSellItem,
      successMessage,
      shops,
    },
  } = state;
  return {
    activeItem,
    buying: activeItem.forSale,
    isFetchingSellItem,
    shops,
    successMessage,
    quantities: getQuantities(state),
  };
};

export default connect(mapStateToProps)(ExchangeUIContainer);